import type { RuleCondition } from '../../data/schemas';
import { assertNever } from '../../types/ids';
import type { EvaluationContext } from '../../types/result';

function compare(
  operator: 'eq' | 'neq' | 'gte' | 'lte' | 'in',
  actual: unknown,
  expected: unknown,
): boolean {
  switch (operator) {
    case 'eq':
      return actual === expected;
    case 'neq':
      return actual !== expected;
    case 'gte':
      return typeof actual === 'number' && typeof expected === 'number' && actual >= expected;
    case 'lte':
      return typeof actual === 'number' && typeof expected === 'number' && actual <= expected;
    case 'in':
      return Array.isArray(expected) && expected.includes(actual);
    default:
      return assertNever(operator, 'Unbekannter Operator');
  }
}

/**
 * Wertet eine einzelne Regelbedingung gegen den Auswertungskontext aus (Abschnitt 17.3).
 * Unterstützt question_score, context_value, overall_percentage, cluster_percentage, compound_any.
 */
export function evaluateRuleCondition(
  condition: RuleCondition,
  context: EvaluationContext,
): boolean {
  switch (condition.type) {
    case 'question_score': {
      const answer = context.answers[condition.questionId];
      if (!answer) return false;
      return compare(condition.operator, answer.score, condition.value);
    }
    case 'context_value': {
      const value = context.context[condition.field];
      if (value === undefined) return false;
      return compare(condition.operator, value, condition.value);
    }
    case 'overall_percentage': {
      return compare(condition.operator, context.overall.percentage, condition.value);
    }
    case 'cluster_percentage': {
      const cluster = context.clusters[condition.clusterId];
      if (!cluster) return false;
      return compare(condition.operator, cluster.percentage, condition.value);
    }
    case 'compound_any': {
      return condition.conditions.some((nested) => evaluateRuleCondition(nested, context));
    }
    default:
      return assertNever(condition, 'Unbekannter Regeltyp');
  }
}

/** Wertet eine Liste von Bedingungen mit `all` (UND) oder `any` (ODER) aus. */
export function evaluateConditionGroup(
  conditions: RuleCondition[],
  mode: 'all' | 'any',
  context: EvaluationContext,
): boolean {
  if (conditions.length === 0) return true;
  return mode === 'all'
    ? conditions.every((condition) => evaluateRuleCondition(condition, context))
    : conditions.some((condition) => evaluateRuleCondition(condition, context));
}
